import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { DateOrTimeFormatOption, DateTimeFormatFormsService } from '@sphere/platform';
import { datetimeFormatValidator, dotNetDateTimeFormatValidator } from './date-time-format.validator';
import { format } from 'date-fns';
import { Observable, timer } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';

import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { PushPipe } from '@ngrx/component';
import { DronaDateComponent } from '@sphere/platform';

export type DateFormatSelectorConfig = FormlyFieldConfig & {
    props: {
        replaceMap?: { match: string; replaceWith: string }[];
        standardDateFormatOptions?: boolean;
        includeFreeFormOption?: boolean;
        useDotNetPatternValidation?: boolean;
        skipPreview?: boolean;
        options?: DateOrTimeFormatOption[];
    };
};

@Component({
    selector: 'sphere-date-format-selector',
    templateUrl: './date-format-selector.component.html',
    imports: [MatFormField, MatLabel, MatSelect, ReactiveFormsModule, MatOption, DronaDateComponent, MatHint, MatInput, MatError, PushPipe]
})
export class DateFormatSelectorComponent extends FieldType<DateFormatSelectorConfig> implements OnInit, AfterViewInit {
    private service = inject(DateTimeFormatFormsService);

    private readonly destroyRef = inject(DestroyRef);
    private readonly empty: DateOrTimeFormatOption = {
        displayName: '-- None --',
        value: null as any
    };
    public readonly advancedFormat = 'advancedFormat';
    public readonly advancedFormatDefault = 'yyyy-MM-dd';
    public readonly defaultFormat = 'dd/MM/yy';

    public dateFormatOptions!: DateOrTimeFormatOption[];

    public sampleDate$!: Observable<Date>;

    public dateFormatControl = new FormControl<string>('');
    public advancedFormatControl = new FormControl<string>('');

    public ngOnInit(): void {
        const _preset: DateOrTimeFormatOption[] = this.props.required ? [] : [this.empty];
        if (this.props.options?.length) {
            this.dateFormatOptions = [..._preset, ...this.props.options];
        } else {
            this.dateFormatOptions = [..._preset, ...this.service.getDateFormatOptions(this.props.standardDateFormatOptions as any)];
            if (this.props.replaceMap && this.props.replaceMap.length) {
                this.dateFormatOptions = this.dateFormatOptions.map(format => {
                    let formatValue: string = format.value;
                    for (const replacement of this.props.replaceMap as any) {
                        formatValue = formatValue.replace(new RegExp(replacement.match, 'gm'), replacement.replaceWith);
                    }
                    return { ...format, value: formatValue };
                });
            }
        }
        this.sampleDate$ = this.service.getSampleDate();
        if (this.props.required) {
            // required validator
        }
        if (this.formControl.value) {
            if (this.props.replaceMap && this.props.replaceMap.length) {
                let value: string = this.formControl.value;
                for (const replacement of this.props.replaceMap) {
                    value = value.replace(new RegExp(replacement.match, 'gm'), replacement.replaceWith);
                }
                this.formControl.setValue(value, { emitEvent: false });
            }
            this.updateValuesFromParent(this.formControl.value);
        } else {
            // has no default value
        }

        this.valueChangeHandler();
    }

    public ngAfterViewInit() {
        timer(100)
            .pipe(
                take(1),
                tap(() => this.advancedFormatControl.updateValueAndValidity())
            )
            .subscribe();
    }

    private updateValuesFromParent(format: string) {
        const match = this.dateFormatOptions.find(preset => preset.value === format);
        if (match) {
            this.setValidators(match.value);
            this.dateFormatControl.setValue(match.value);
        } else if (this.props.includeFreeFormOption) {
            this.dateFormatControl.setValue(this.advancedFormat);
            this.setValidators(this.advancedFormat, format);
            this.advancedFormatControl.setValue(format);
        } else {
            this.setValidators(this.defaultFormat);
            this.dateFormatControl.setValue(this.defaultFormat);
        }
    }
    private setValidators(format: string, advancedFormat?: string) {
        if (format === this.advancedFormat) {
            this.advancedFormatControl.setValue(advancedFormat || this.advancedFormatDefault);
            if (this.props.useDotNetPatternValidation) {
                this.advancedFormatControl.setValidators([Validators.required, dotNetDateTimeFormatValidator()]);
            } else {
                this.advancedFormatControl.setValidators([Validators.required, datetimeFormatValidator()]);
            }
        } else {
            this.setParentFormControlAsPreset(format);
            this.advancedFormatControl.setValidators(null);
            this.formControl.setErrors(null);
        }
    }

    private valueChangeHandler() {
        this.dateFormatControl.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(format => {
                    this.setValidators(format as string);
                })
            )
            .subscribe();

        this.advancedFormatControl.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.isAdvancedFormat),
                tap(advancedFormat => {
                    this.setParentFormControlAsFreeForm(advancedFormat as string);
                    if (this.advancedFormatControl.errors) {
                        this.formControl.setErrors(this.advancedFormatControl.errors);
                    } else {
                        this.formControl.setErrors(null);
                    }
                })
            )
            .subscribe();
    }

    private setParentFormControlAsPreset(value: string) {
        console.debug(`updating parent form with preset`, value);
        this.formControl.setValue(value);
    }
    private setParentFormControlAsFreeForm(format: string) {
        console.debug(`updating parent form with custom freeform format`, format);
        this.formControl.setValue(format);
    }

    public get isAdvancedFormat(): boolean {
        return this.dateFormatControl?.value === this.advancedFormat;
    }

    public get advancedFormatPreview() {
        try {
            const formatValue = this.advancedFormatControl.value;
            if (!formatValue) {
                return null;
            }
            const formattedNow = format(new Date(), formatValue);
            if (formattedNow && typeof formattedNow === 'string') {
                return formattedNow;
            }
            return null;
        } catch (e) {
            return null;
        }
    }
}
